import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button, Typography, Input, Grid, message, Empty, Pagination, Space, Flex, Card, Tooltip, Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, SearchOutlined, EllipsisOutlined } from '@ant-design/icons';
import { deleteKnowledgeBase, getknowledgeDatabases, duplicateKnowledgeBase } from './KnowledgeDatabaseServices';
import { debounce } from 'lodash';
import AddKnowledgeBaseModal from './AddKnowledgeBaseModal/AddKnowledgeBaseModal.js';
import DetailModal from './DetailModal/DetailModal.js';
import KnowledgeCard from './KnowledgeCard/KnowledgeCard.js';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext.js';
import KnowledgeBaseSkeleton from './KnowledgeBaseSkeleton';
import useSocket from '../../Hooks/useSocket';
import useSocketSubscription from '../../Hooks/useSocketSubscription';
import useSocketEvent from '../../Hooks/useSocketEvent';
import useVisualHighlight from '../../Hooks/useVisualHighlight';
import { Resource, Action } from '../../Constants/socket';
import { transformKnowledgeBaseData } from './utils/transformations';
import { resolveBackendMessage } from '../../Utils/apiMessages';
import SharedFilterBar from '../Shared/Filters/SharedFilterBar';
import { knowledgeBaseFilterFields } from '../Shared/Filters/filterConfig';
import useFilterOptionFields, { FILTER_OPTION_RESOURCE } from '../Shared/Filters/useFilterOptionFields';
import ExportButton from '../DataExportImport/ExportButton';
import ImportButton from '../DataExportImport/ImportButton';

const { Title } = Typography;
const { useBreakpoint } = Grid;

const KNOWLEDGE_BASE_FILTER_OPTION_RESOURCES = {
    agent_id: FILTER_OPTION_RESOURCE.AGENT
};

const KnowledgeDatabasePage = () => {
    const { t } = useTranslation();
    const { setLoading, user: { id: currentUserId } = {} } = useGlobal();
    const [skeleton, setSkeleton] = useState(true);
    const [expandedKB, setExpandedKB] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [knowledgeBase, setKnowledgeBase] = useState([]);
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const navigate = useNavigate();
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 25,
        total: 0
    });
    const [kbChipFilters, setKbChipFilters] = useState([]);
    const [kbNormalizedFilters, setKbNormalizedFilters] = useState({});
    const [sortBy, setSortBy] = useState(undefined);
    const [sortDirection, setSortDirection] = useState(undefined);
    const [isMoreActionsOpen, setIsMoreActionsOpen] = useState(false);
    const screens = useBreakpoint();
    
    // Visual highlights for real-time socket events
    const { highlightRow, getHighlightClass } = useVisualHighlight();

    const { socket } = useSocket();
    const isSubscribed = useSocketSubscription(Resource.USER, currentUserId ? [currentUserId] : null, true);
    const knowledgeBaseIds = useMemo(() => knowledgeBase.map(kb => kb.id), [knowledgeBase]);
    useSocketSubscription(Resource.KNOWLEDGE_BASE, knowledgeBaseIds);
    const filterFields = useFilterOptionFields(knowledgeBaseFilterFields, KNOWLEDGE_BASE_FILTER_OPTION_RESOURCES);

    const debouncedFetch = useRef(
        debounce((page, size, search, filtersPayload, sort_by, sort_direction) => {
            const fetchData = async () => {
                try {
                    setLoading(true);
                    const { data, success, metadata, ...payload } = await getknowledgeDatabases({
                        page,
                        size,
                        search,
                        filters: filtersPayload,
                        sort_by,
                        sort_direction,
                    });

                    if (success) {
                        const transformedData = data.map(transformKnowledgeBaseData);
                        setKnowledgeBase(transformedData);
                        setPagination({
                            current: metadata.page,
                            pageSize: metadata.page_size,
                            total: metadata.total
                        });
                    } else {
                        const text = resolveBackendMessage(t, payload);
                        if (text) message.error({ content: text, key: 'loading' });
                    }
                } catch (error) {
                    message.error({
                        content: resolveBackendMessage(t, error?.response?.data) || t('error_fetching_knowledge_bases'),
                        key: 'loading'
                    });
                } finally {
                    setLoading(false);
                    setSkeleton(false);
                }
            };
            fetchData();
        }, 500)
    ).current;

    useEffect(() => {
        return () => {
            debouncedFetch.cancel();
        };
    }, []);

    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchText(value);
        setPagination(prev => ({ ...prev, current: 1 }));
        debouncedFetch(1, pagination.pageSize, value, kbNormalizedFilters, sortBy, sortDirection);
    };

    useEffect(() => {
        debouncedFetch(pagination.current, pagination.pageSize, searchText, kbNormalizedFilters, sortBy, sortDirection);
    }, [pagination.current, pagination.pageSize, searchText, kbNormalizedFilters, debouncedFetch, sortBy, sortDirection]);

    const handlePageChange = (page, pageSize) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize
        }));
    };

    const handleDelete = useCallback(async (id) => {
        try {
            setLoading(true);
            const { success, data, ...payload } = await deleteKnowledgeBase(id);

            if (success) {
                debouncedFetch(pagination.current, pagination.pageSize, searchText, kbNormalizedFilters, sortBy, sortDirection);
            } else {
                const text = resolveBackendMessage(t, payload);
                if (text) message.error({ content: text, key: 'deleting' });
            }
        } catch (error) {
            message.error({
                content: resolveBackendMessage(t, error?.response?.data) || t('error_deleting_knowledge_base'),
                key: 'deleting'
            });
        } finally {
            setLoading(false);
        }
    }, [pagination, searchText, debouncedFetch, setLoading, t, sortBy, sortDirection, kbNormalizedFilters]);

    const handleDuplicate = useCallback(async (item) => {
        try {
            setLoading(true);
            const payload = await duplicateKnowledgeBase(item.id);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            debouncedFetch(pagination.current, pagination.pageSize, searchText, kbNormalizedFilters, sortBy, sortDirection);
        } catch (error) {
            message.error({
                content: resolveBackendMessage(t, error?.response?.data) || t('error_duplicating_knowledge_base'),
                key: 'duplicating'
            });
        } finally {
            setLoading(false);
        }
    }, [pagination.current, pagination.pageSize, searchText, kbNormalizedFilters, debouncedFetch, setLoading, sortBy, sortDirection, t]);

    // Handle new knowledge base
    const handleKnowledgeBaseCreated = useCallback((data) => {
        debouncedFetch(pagination.current, pagination.pageSize, searchText, kbNormalizedFilters, sortBy, sortDirection);

        // Subscribe to new knowledge base from server_datetime to cover the readiness gap
        if (data?.id && data?.server_datetime && socket) {
            socket.subscribeToResource(Resource.KNOWLEDGE_BASE, [data.id], data.server_datetime);
        }
        if (data?.id) highlightRow(data.id);
    }, [pagination.current, pagination.pageSize, searchText, kbNormalizedFilters, debouncedFetch, sortBy, sortDirection, socket, highlightRow]);

    // Handle updated knowledge base
    const handleKnowledgeBaseUpdated = useCallback((data) => {
        setKnowledgeBase(prev =>
            prev.map(kb => kb.id === data.id ? transformKnowledgeBaseData(data) : kb)
        );
        if (data?.id) highlightRow(data.id, 'UPDATE');
    }, [highlightRow]);

    // Handle deleted knowledge base
    const handleKnowledgeBaseDeleted = useCallback((data) => {
        debouncedFetch(pagination.current, pagination.pageSize, searchText, kbNormalizedFilters, sortBy, sortDirection);
    }, [pagination.current, pagination.pageSize, searchText, kbNormalizedFilters, debouncedFetch, sortBy, sortDirection]);

    const handleChipSortChange = useCallback(({ sort_by, sort_direction }) => {
        setSortBy(sort_by);
        setSortDirection(sort_direction);
        setPagination(prev => ({ ...prev, current: 1 }));
    }, []);

    // Register socket event listeners
    useSocketEvent(Resource.KNOWLEDGE_BASE, Action.CREATE, handleKnowledgeBaseCreated);
    useSocketEvent(Resource.KNOWLEDGE_BASE, Action.UPDATE, handleKnowledgeBaseUpdated);
    useSocketEvent(Resource.KNOWLEDGE_BASE, Action.DELETE, handleKnowledgeBaseDeleted);

    return (
        <Flex style={{ padding: 0, flexGrow: 1 }} gap={16} vertical>
            <Card
                bordered={false}
                title={t('knowledge_base')} extra={
                    <Space size={4}>
                        <Button
                            color="primary"
                            icon={<PlusOutlined />}
                            variant="link"
                            onClick={() => setIsAddModalVisible(true)}
                        >
                            {!screens.xs && t('add_knowledge_base')}
                        </Button>
                        <Popover
                            trigger="click"
                            placement="bottomRight"
                            open={isMoreActionsOpen}
                            onOpenChange={setIsMoreActionsOpen}
                            content={
                                <Space direction="vertical" style={{ minWidth: 140 }} onClickCapture={() => setIsMoreActionsOpen(false)}>
                                    <ImportButton resource={Resource.KNOWLEDGE_BASE} type="text" size="small" />
                                    <ExportButton resource={Resource.KNOWLEDGE_BASE} filters={kbNormalizedFilters} type="text" size="small" />
                                </Space>
                            }
                        >
                            <Tooltip title={t('more_actions')}>
                                <Button
                                    type="text"
                                    icon={<EllipsisOutlined />}
                                    size="medium"
                                />
                            </Tooltip>
                        </Popover>
                    </Space>
                }
                styles={{
                    body: {
                        padding: 16
                    }
                }}
            >
                <Flex
                    gap="middle"
                    wrap="wrap"
                    align="center"
                    justify="space-between"
                >
                    <Flex
                        flex={{ xs: '100%', md: 1 }}
                        style={{ flexGrow: 1 }}
                    >
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder={t('search_knowledge_base')}
                            size="medium"
                            onChange={handleSearchChange}
                            onFocus={() => setIsInputFocused(true)}
                            onBlur={() => setIsInputFocused(false)}
                            style={{
                                width: isInputFocused ? '100%' : (screens.xs ? '100%' : 400),
                                transition: 'width 0.4s ease-in-out'
                            }}
                        />
                    </Flex>

                    <SharedFilterBar
                        fields={filterFields}
                        value={kbChipFilters}
                        onChange={(nextFilters, normalized) => {
                            setKbChipFilters(nextFilters);
                            setKbNormalizedFilters(normalized);
                            setPagination(prev => ({ ...prev, current: 1 }));
                            debouncedFetch(1, pagination.pageSize, searchText, normalized, sortBy, sortDirection);
                        }}
                        sortFields={[
                            { value: 'name', labelKey: 'name' },
                            { value: 'created_at', labelKey: 'created_at' },
                            { value: 'updated_at', labelKey: 'updated_at' },
                        ]}
                        sortValue={{ sort_by: sortBy, sort_direction: sortDirection }}
                        onSortChange={handleChipSortChange}
                    />
                </Flex>
            </Card>

            {skeleton ? (
                <KnowledgeBaseSkeleton />
            ) : (
                knowledgeBase.length === 0 ? (
                    <Empty
                        style={{ flexGrow: 1 }}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <Space direction="vertical" align="center">
                                <Typography.Title level={5}>{t('no_knowledge_bases_found')}</Typography.Title>
                                <Typography.Text type="secondary">
                                    {t('click_add_agent_to_create_your_first_one')}
                                </Typography.Text>
                            </Space>
                        }
                    />
                ) : (
                    <Flex gap={16} wrap justify="center" align="flex-start">
                        {knowledgeBase.map(item => (
                            <KnowledgeCard
                                key={item.id}
                                item={item}
                                onExpand={() => setExpandedKB(item)}
                                onDelete={handleDelete}
                                onDuplicate={handleDuplicate}
                                className={getHighlightClass ? getHighlightClass(item.id, 'socket-highlight-target-card') : ''}
                            />
                        ))}
                    </Flex>
                )
            )}
            <Flex justify="center">
                <Pagination
                    size="small"
                    simple={screens.xs}
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={handlePageChange}
                    showSizeChanger={!screens.xs}
                    showQuickJumper={!screens.xs}
                    pageSizeOptions={['10', '25', '50', '100']}
                    showTotal={(total, range) =>
                        `${range?.[0] || 0}-${range?.[1] || 0} ${t('of')} ${total} ${t('items')}`
                    }
                />
            </Flex>

            <AddKnowledgeBaseModal
                visible={isAddModalVisible}
                onClose={() => setIsAddModalVisible(false)}
                navigate={navigate}
            />

            <DetailModal
                item={expandedKB}
                onClose={() => setExpandedKB(null)}
            />
        </Flex>
    );
};

export default KnowledgeDatabasePage;
